import { apiClient } from './api';
import { currentOfflineAuthorizationEnvelope } from '../offline/authEnvelope';
import { commitLocalCommand } from '../offline/commandBus';
import {
  registerSyncHandler,
  requestSync,
  type SyncAck,
} from '../offline/syncCoordinator';
import { sha256Canonical } from '../offline/crypto';
import { getAllByIndex } from '../offline/db';

let registered = false;

function commandHeaders(command: any) {
  return {
    'Idempotency-Key': command.idempotency_key,
    'X-Device-ID': command.device_id,
    'X-Command-Schema': command.schema_version,
    'X-App-Version': command.app_version,
    'X-Payload-Hash': command.payload_hash,
  };
}

export async function queueHarvestInspection(
  userId: string,
  notificationId: string,
  inspectionNotes = 'Inspected by field agent',
) {
  if (!userId) throw new Error('Authenticated user is required');
  if (!notificationId) throw new Error('Harvest notification ID is required');

  const envelope = await currentOfflineAuthorizationEnvelope(userId);

  if (!envelope) {
    throw new Error(
      'No offline authorization envelope is available on this device',
    );
  }

  const payload = {
    notification_id: notificationId,
    inspection_notes: inspectionNotes,
    expected_status: 'PENDING',
  };

  const result = await commitLocalCommand({
    tenant_id: envelope.tenant_id,
    user_id: userId,
    resource_type: 'harvest_notification',
    resource_id: notificationId,
    operation: 'INSPECT',
    offline_action: 'INSPECT_HARVEST_NOTICE',
    payload,
    priority: 'P1',
    domain_record: {
      id: notificationId,
      status: 'INSPECTED_PENDING_SYNC',
      expected_status: 'PENDING',
      inspection_notes: inspectionNotes,
      inspected_locally_at: new Date().toISOString(),
    },
  });

  requestSync('HARVEST_INSPECTION_COMMITTED');

  return result;
}


export async function loadHarvestInspectionOverlays(
  userId: string,
): Promise<any[]> {
  const envelope =
    await currentOfflineAuthorizationEnvelope(userId);

  if (!envelope) return [];

  return getAllByIndex<any>(
    'domain_records',
    'byTenantUserType',
    IDBKeyRange.only([
      envelope.tenant_id,
      userId,
      'harvest_notification',
    ]),
  );
}

export function registerFieldAgentOfflineHandlers(): void {
  if (registered) return;
  registered = true;

  registerSyncHandler(
    'harvest_notification',
    'INSPECT',
    async command => {
      const payload: any = command.payload;

      const response = await apiClient.patch(
        `/harvest-notifications/${command.resource_id}/inspect`,
        {
          notification_id: command.resource_id,
          inspection_notes:
            payload?.inspection_notes || 'Inspected by field agent',
          expected_status: 'PENDING',
        },
        {
          headers: commandHeaders(command),
        },
      );

      const resultHash = await sha256Canonical(response.data);

      const ack: SyncAck = {
        ack_id:
          response.data?.command_id ||
          command.command_id,
        command_id: command.command_id,
        server_resource_id:
          response.data?.notification?.id ||
          command.resource_id,
        server_revision:
          response.data?.notification?.updated_at ||
          null,
        accepted_at:
          response.data?.notification?.updated_at ||
          new Date().toISOString(),
        result_hash: resultHash,
        status: 'ACKNOWLEDGED',
        resource_type: command.resource_type,
        resource_id: command.resource_id,
      };

      return ack;
    },
  );
}
